<script setup lang="ts">
import device from '@@/bits/device'
import OzOverlay from '@@/library/v4/components/OzOverlay.vue'
import type { VueCssClass } from '@@/types'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     *  TailwindCSS width class.
     *
     *  - `w-99` is for whiteboard card templates panel
     *  - `w-100` is for surface panels when on native tablet
     *  - `w-120` is for surface panels
     *  - `w-135` is for dashboard layout picker
     */
    widthClass?: 'w-99' | 'w-100' | 'w-120' | 'w-135'
    /**
     * Extra TailwindCSS classes for the modal container.
     */
    containerClasses?: VueCssClass
    darkMode?: boolean | 'auto'
    /**
     * Determines whether the modal should have a rounded top inner corner
     */
    topInnerCornerRadius?: 0 | 12
    /**
     * Determines whether the modal should have a rounded bottom inner corner
     */
    bottomInnerCornerRadius?: 0 | 12
    /**
     *  TailwindCSS z-index class.
     *
     *  - `z-modal2` is for all dialogs
     *  - `z-sidepanel` is for surface sidepanels
     */
    zIndexClass?: 'z-modal2' | 'z-sidepanel'
    isBodyScrollable?: boolean
    hideScrollbar?: boolean
    /**
   * Determines whether the scrollbar should overlay on top of content,
    hideScrollbar must be false for this to work.
    This is only supported on Chrome.
   */
    scrollbarOverlay?: boolean
    /**
     * Determines which direction the OzSideModal should slide in
     */
    slideDirection?: OzSideModalSlideDirection
    /**
     * Determines CSS position
     */
    position?: 'fixed' | 'absolute'
    /**
     * Determines whether the scrim should be shown
     */
    xScrim?: boolean
  }>(),
  {
    widthClass: 'w-120',
    containerClasses: undefined,
    darkMode: 'auto',
    topInnerCornerRadius: 12,
    bottomInnerCornerRadius: 0,
    zIndexClass: undefined,
    isBodyScrollable: true,
    hideScrollbar: false,
    scrollbarOverlay: false,
    slideDirection: OzSideModalSlideDirection.EndToStart,
    position: 'fixed',
    xScrim: true,
  },
)

const scrollbarStyles = computed(() => {
  const defaultScrollbarStyles = [
    {
      'overflow-auto': props.isBodyScrollable,
      'h-full': !props.isBodyScrollable,
    },
    {
      'scrollbar-regular dark:scrollbar-regular-dark': props.darkMode === 'auto',
      'scrollbar-regular': props.darkMode === false,
      'dark:scrollbar-regular-dark': props.darkMode === true,
    },
  ]
  if (props.hideScrollbar) {
    return 'no-scrollbar overflow-auto'
  } else if (props.scrollbarOverlay && device.chrome) {
    return ['scrollbar-overlay', ...defaultScrollbarStyles]
  } else {
    return defaultScrollbarStyles
  }
})

defineEmits(['scrim-click', 'scrim-esc', 'transition:after-enter'])
</script>

<script lang="ts">
export enum OzSideModalSlideDirection {
  EndToStart = 'end-to-start',
  StartToEnd = 'start-to-end',
}

export default {}
</script>

<template>
  <Transition name="slide" appear @after-enter="$emit('transition:after-enter', $event)">
    <OzOverlay
      role="dialog"
      :should-fade-in="false"
      :z-index-class="zIndexClass"
      :dark-mode="darkMode"
      :position="position"
      :scrim="xScrim ? 'modal' : null"
      @scrim-click="$emit('scrim-click')"
      @scrim-esc="$emit('scrim-esc')"
    >
      <div
        :class="[
          'modal-container',
          // Slide direction
          {
            'start-to-end': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'end-to-start': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          // Positioning
          position === 'fixed' ? 'fixed' : 'absolute',
          'inset-y-0',
          // Position after sliding
          {
            'start-0': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'end-0': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          // Layout
          widthClass,
          'flex',
          'flex-col',
          'overflow-hidden',
          topInnerCornerRadius === 12 && {
            'rounded-se-xl': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'rounded-ss-xl': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          bottomInnerCornerRadius === 12 && {
            'rounded-ee-xl': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'rounded-es-xl': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          // Shadow
          'shadow-elevation-3',
          // Background
          {
            'bg-light-ui-100 dark:bg-dark-ui-100': darkMode === 'auto',
            'bg-light-ui-100': darkMode === false,
            'bg-dark-ui-100': darkMode === true,
          },
          containerClasses,
        ]"
      >
        <div v-if="$slots.header">
          <slot name="header" />
        </div>
        <div :class="scrollbarStyles">
          <slot name="body" />
        </div>
      </div>
    </OzOverlay>
  </Transition>
</template>

<style lang="postcss" scoped>
.slide-enter-active,
.slide-enter-active > .modal-container {
  transition: all 0.3s cubic-bezier(0, 0, 0.2, 1);

  @media (prefers-reduced-motion) {
    transition-duration: 0s;
  }
}

.slide-leave-active,
.slide-leave-active > .modal-container {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);

  @media (prefers-reduced-motion) {
    transition-duration: 0s;
  }
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
}

.slide-enter > .modal-container,
.slide-leave-to > .modal-container {
  &.end-to-start {
    transform: translateX(100%);

    [dir='rtl'] & {
      transform: translateX(-100%);
    }
  }

  &.start-to-end {
    transform: translateX(-100%);

    [dir='rtl'] & {
      transform: translateX(100%);
    }
  }
}
</style>
